/**
 * Cross-route chat handoff queue (PD-6).
 *
 * The studio dialog runs on `/files`, `/projects/:id`, etc. When a generative
 * action fires (composer submit, Animate handoff, Variations) the dialog
 * closes and React Router navigates to the chat tree (`/create?session=X`
 * or `/?planId=...`). The chat tree mounts AFTER navigation completes, so
 * `chat.sendMessage(...)` cannot be called synchronously at the navigate
 * site — it would land in the wrong session's queue.
 *
 * This store is a single-slot, in-memory queue: the dialog `push()`es a
 * handoff before navigating; the destination page mounts a consumer that
 * `consume()`s it once the matching session is ready.
 *
 * Idempotent. In-memory only — on hard refresh the pending handoff is
 * dropped; users retry. The 5-second staleness check (`pushedAt`) gives
 * the consumer a defensive way to discard handoffs whose target page
 * never mounted.
 *
 * Foundation only — no callers yet. T2.1 wires the dialog → push, and
 * T3.1/T3.2 wire the consumer.
 */
import type { UIMessagePart } from 'ai';
import { create } from 'zustand';

import { registerWorkspaceReset } from '@/lib/workspace/workspaceResetRegistry';

export interface ChatHandoff {
  sessionId: string;
  parts: UIMessagePart<Record<string, unknown>, Record<string, unknown>>[];
  pageContext?: {
    attachedImage?: { url: string; originalPrompt?: string };
  };
  pushedAt: number;
}

interface State {
  pending: ChatHandoff | null;
  lastFailed: ChatHandoff | null;
  push: (handoff: Omit<ChatHandoff, 'pushedAt'>) => void;
  consume: () => ChatHandoff | null;
  markFailed: (handoff: ChatHandoff) => void;
  clearFailed: () => void;
}

export const useChatHandoffStore = create<State>((set, get) => ({
  pending: null,
  lastFailed: null,
  push: (handoff) => set({ pending: { ...handoff, pushedAt: Date.now() } }),
  consume: () => {
    const current = get().pending;
    if (current) set({ pending: null });
    return current;
  },
  markFailed: (handoff) => set({ lastFailed: handoff }),
  clearFailed: () => set({ lastFailed: null }),
}));

registerWorkspaceReset('chat-handoff-store', () => {
  useChatHandoffStore.setState({ pending: null, lastFailed: null });
});

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as unknown as { __chatHandoffStore: typeof useChatHandoffStore }).__chatHandoffStore =
    useChatHandoffStore;
}
